import { denormalizeAmount } from '@revolugo/common/utils'
import { type Ref, computed, ref } from 'vue'

import type { Currency } from '@revolugo/common/constants'
import type { CurrencyGraphQl, MoneyObject } from '@revolugo/graphql/private'

export function useMoney(amount: MoneyObject | null, locale: Ref<string>) {
  const money = ref<MoneyObject | null>(amount)
  const amountValue = computed(() => {
    if (!money.value) {
      return null
    }
    return money.value.value
  })
  const currency = computed(() => {
    if (!money.value) {
      return null
    }
    return money.value.currency
  })

  const formattedValue = computed(() => {
    if (!money.value) {
      return null
    }
    const denormalizedAmount = denormalizeAmount({
      amount: money.value.value,
      currency: money.value.currency as unknown as Currency,
    })

    return new Intl.NumberFormat(locale.value, {
      currency: money.value.currency,
      style: 'currency',
    }).format(denormalizedAmount)
  })

  function setMoney(newMoney: MoneyObject | null) {
    money.value = newMoney
  }

  function updateValue(newValue?: string | number | null) {
    if (!money.value) {
      return
    }

    money.value.value = Number(newValue)
  }

  function updateCurrency(newCurrency: CurrencyGraphQl) {
    if (!money.value) {
      return
    }
    money.value.currency = newCurrency
  }

  return {
    amountValue,
    currency,
    formattedValue,
    money,
    setMoney,
    updateCurrency,
    updateValue,
  }
}
